import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  Container,
  Button,
  Typography,
  Box,
} from "@mui/material";
import ImageUploader, {
  uploadBas64ToS3,
} from "../../components/utilities/ImageUploader";
import { Formik } from "formik";
import { useParams } from "react-router-dom";
import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import GenericPicker from "../../components/utilities/GenericPicker";
import * as Yup from "yup";
import { useConfirm } from "material-ui-confirm";
import { BCPFloppyDisk, BCPTrash } from "../../components/icons/bcp/BCPIcons";
import {
  deleteData,
  getData,
  postData,
} from "../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";

export default function TagEditor() {
  const [loading, setLoading] = useState(true);
  const [tag, setTag] = useState(null);
  const { tagId } = useParams();
  const confirm = useConfirm();

  useEffect(() => {
    const go = async () => {
      let apiTag;

      if (tagId) {
        try {
          apiTag = await getData(`/pages/tags/${tagId}`, {
            queryStringParameters: {
              env: getEnvironmentVariable("COMPANY_FLAG"),
            },
          });
        } catch (err) {
          postNotification("error", err);
        }
      } else {
        apiTag = {
          permalink: "",
          title: "",
          sortOrder: "",
          isPrimaryTag: false,
          imageId: "",
        };
      }

      setTag(apiTag);
      setLoading(false);
    };

    go();
  }, [tagId]);

  return (
    <Box width="100%">
      {Boolean(loading) && <ModalLoadingFull />}

      <Container
        maxWidth="lg"
        sx={{
          pt: 2,
          pb: 2,
        }}
      >
        <Grid container gap={2} alignItems="center" sx={{ p: 2 }}>
          <Grid item xs={12} p={2}>
            <Typography mb={2} variant="h3">
              Tag Editor
            </Typography>
          </Grid>

          <Grid item xs={12} p={2}>
            <Formik
              initialValues={{
                permalink: tag?.permalink || "",
                title: tag?.title || "",
                sortOrder: tag?.sortOrder || "",
                isPrimaryTag: tag?.isPrimaryTag || false,
                imageId: tag?.imageId || "",
              }}
              validationSchema={Yup.object({
                title: Yup.string().required("Required field."),
                permalink: Yup.string()
                  .max(40)
                  .matches(
                    /^[a-z0-9_-]*$/,
                    'Must contain only alphanumeric, "_", and "-"'
                  ),
              })}
              enableReinitialize
              onSubmit={async (values) => {
                try {
                  setLoading(true);

                  let sanitizedValues = {
                    ...values,
                  };

                  if (sanitizedValues.tagImage) {
                    let newFile = await uploadBas64ToS3(
                      sanitizedValues.tagImage
                    );
                    delete sanitizedValues.tagImage;
                    if (newFile.fileId) {
                      sanitizedValues.imageId = newFile.fileId;
                    }
                  }

                  if (tag.id) {
                    await postData(`/pages/tags/${tag.id}`, {
                      body: {
                        env: getEnvironmentVariable("COMPANY_FLAG"),

                        set: sanitizedValues,
                      },
                    });

                    postNotification("success", "Tag was updated.");
                  } else {
                    let newTag = await postData("/pages/tags", {
                      body: {
                        env: getEnvironmentVariable("COMPANY_FLAG"),

                        ...sanitizedValues,
                      },
                    });

                    postNotification("success", "Tag was created.");

                    window.location.assign(`/cms/tag/${newTag.id}`);
                    return;
                  }
                } catch (err) {
                  postNotification("error", err);
                }

                setLoading(false);
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                touched,
                values,
                setFieldValue,
              }) => (
                <>
                  <form onSubmit={handleSubmit} sx={{ p: "8px" }}>
                    <Grid container spacing={2} justifyContent="center">
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Grid container gap={1}>
                          <Grid item xs={12}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              required
                              label="Tag Title"
                              name="title"
                              id="title"
                              value={values.title}
                              onChange={(e) => {
                                handleChange(e);
                                if (!touched.permalink && !tag.id) {
                                  let permalink = e.target.value
                                    .split(" ")
                                    .join("-")
                                    .toLowerCase()
                                    .replace(/[^a-z0-9_-]+/g, "");

                                  setFieldValue("permalink", permalink, false);
                                }
                              }}
                              onBlur={handleBlur}
                              error={Boolean(touched.title && errors.title)}
                              helperText={Boolean(
                                touched.title && errors.title
                              )}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              label="Permalink"
                              name="permalink"
                              id="permalink"
                              value={values.permalink}
                              onChange={(e) => {
                                e.target.value = e.target.value
                                  .split(" ")
                                  .join("-")
                                  .toLowerCase()
                                  .replace(/[^a-z0-9_-]+/g, "");
                                handleChange(e);

                                if (!touched.title && !tag.id) {
                                  let title = e.target.value
                                    .split("-")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1)
                                    )
                                    .join(" ");

                                  setFieldValue("title", title);
                                }
                              }}
                              onBlur={handleBlur}
                              error={Boolean(
                                touched.permalink && errors.permalink
                              )}
                              helperText={Boolean(
                                touched.permalink && errors.permalink
                              )}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              type="number"
                              label="Sort Order"
                              name="sortOrder"
                              id="sortOrder"
                              value={values.sortOrder}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={Boolean(
                                touched.sortOrder && errors.sortOrder
                              )}
                              helperText={Boolean(
                                touched.sortOrder && errors.sortOrder
                              )}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <GenericPicker
                              label="Primary Tag"
                              name="isPrimaryTag"
                              id="isPrimaryTag"
                              value={values.isPrimaryTag}
                              list={[
                                { label: "No", value: false },
                                { label: "Yes", value: true },
                              ]}
                              handleChange={handleChange}
                              onBlur={handleBlur}
                              inclAny={false}
                              error={Boolean(
                                touched.isPrimaryTag && errors.isPrimaryTag
                              )}
                              helperText={Boolean(
                                touched.isPrimaryTag && errors.isPrimaryTag
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <ImageUploader
                          values={values}
                          label="Tag Image"
                          name="tagImage"
                          fileId={values.imageId}
                          setFieldValue={setFieldValue}
                        />
                        <Typography textAlign={"center"} variant="body1">
                          Tag Image
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={tagId ? 8 : 12}>
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          startIcon={<BCPFloppyDisk />}
                        >
                          Save
                        </Button>
                      </Grid>

                      {tagId && (
                        <Grid item xs={12} md={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<BCPTrash />}
                            color="error"
                            onClick={async () => {
                              try {
                                await confirm({
                                  title: `Are you sure?`,
                                  description:
                                    "Are you sure you want to delete this tag?",
                                  dialogProps: {
                                    sx: {
                                      zIndex: 99999,
                                    },
                                  },
                                });
                              } catch (err) {
                                return;
                              }
                              try {
                                await confirm({
                                  title: `Are you really sure?`,
                                  description:
                                    "Are you really sure you want to delete this tag? This can't be undone.",
                                  dialogProps: {
                                    sx: {
                                      zIndex: 99999,
                                    },
                                  },
                                });
                              } catch (err) {
                                return;
                              }
                              try {
                                setLoading(true);
                                await deleteData(`/pages/tags/${tagId}`);
                                postNotification("success", "Tag was deleted.");
                                window.location.assign("/cms/tags");
                                return;
                              } catch (err) {
                                postNotification("error", err);
                              }
                              setLoading(false);
                            }}
                          >
                            Delete
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </form>
                </>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
